import React from "react";
import { styled } from "@linaria/react";
import Footer from "./Footer";
import type { Variant } from "../Button";
import { fonts } from "../Theme";
import Form from "../Form";
interface P {
  children: React.ReactNode;
  className?: string;
  /**
   * null => no submit button
   * false => submit button disabled
   * function returns true => onRequestClose
   * function returns false => no onRequestClose, focuses first field with "error" class
   */
  onSubmit: null | false | (() => boolean | Promise<boolean>);
  onRequestClose: () => void;
  cancellable?: boolean;
  focusCancel?: boolean;
  focusOk?: boolean;
  cancelButtonName?: string;
  okButtonName?: string;
  okButtonVariant?: Variant;
  centerButtons?: boolean;
}

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  margin: 24px 0;
  font: ${fonts.body1};
`;

const CustomForm = styled(Form)`
  display: contents;
`;

const ModalForm = ({
  children,
  className,
  onSubmit,
  onRequestClose,
  focusCancel,
  focusOk,
  cancelButtonName,
  okButtonName,
  cancellable = true,
  centerButtons = false,
  okButtonVariant = "primary",
}: P) => {
  const formRef = React.useRef<HTMLFormElement>(null);
  const [loading, setLoading] = React.useState(false);
  const handleSubmit = React.useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!onSubmit) return;
      setLoading(true);
      try {
        if (await onSubmit()) {
          onRequestClose();
        } else {
          window.setTimeout(() => {
            const firstErroredInput = formRef.current?.querySelector(
              "input.error, select.error, textarea.error",
            );
            const currentFocusedEl = window.document.activeElement;
            if (
              !currentFocusedEl?.classList.contains("error") &&
              firstErroredInput instanceof HTMLElement
            ) {
              window.requestAnimationFrame(() => {
                firstErroredInput.focus({ preventScroll: true });
              });
            }
          }, 50);
        }
        setLoading(false);
      } catch (e) {
        setLoading(false);
        throw e;
      }
    },
    [onRequestClose, onSubmit, setLoading],
  );

  return (
    <CustomForm
      ref={formRef}
      onSubmit={handleSubmit}
      noValidate
      afterMandatoryLabel={
        <Footer
          onOk={onSubmit === null ? null : Boolean(onSubmit)}
          onCancel={cancellable ? onRequestClose : null}
          cancelButtonName={cancelButtonName}
          okButtonName={okButtonName}
          okButtonVariant={okButtonVariant}
          focusCancel={focusCancel}
          focusOk={focusOk}
          loading={loading}
          centerButtons={centerButtons}
        />
      }
    >
      <Content className={className}>{children}</Content>
    </CustomForm>
  );
};

export default ModalForm;
